/**
 * hexLayout — pure geometry for the Alert Hexagon canvas (shared local + cloud).
 *
 *   packCluster()    arranges a cluster's cameras into a flat-top honeycomb.
 *   layoutClusters() flows packed clusters across a virtual canvas (wrapping at
 *                    TARGET_W), giving every hex an ABSOLUTE canvas-space center
 *                    {cx,cy} — the anchor for the floating preview / hover popover.
 *
 * Geometry constants live here (the canonical source) so shared hex components
 * don't depend on any app's local files.
 */
// Flat-top hexagons: wider than tall (the pointy-top orientation rotated 90°).
export const HEX_W = 144;
export const HEX_H = 128;
export const HEX_COL_OVERLAP = 36; // horizontal interlock between columns
export const HEX_CLIP = 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)';

export const HEX_GAP = 12; // vertical gap between hexes in a column
export const STEP_X = HEX_W - HEX_COL_OVERLAP; // column pitch (¾ width → flat-top interlock)
export const STEP_Y = HEX_H + HEX_GAP; // row pitch

export const TITLE_H = 30; // cluster title bar height (above the hexes)
export const CLUSTER_GAP_X = 56;
export const CLUSTER_GAP_Y = 48;
export const TARGET_W = 1280; // virtual width clusters flow into before wrapping
export const CANVAS_PAD = 64;

export interface ClusterInput<T> {
  id: string;
  title: string;
  subtitle?: string;
  items: T[];
}

export interface PositionedHex<T> {
  item: T;
  x: number;
  y: number;
  cx: number;
  cy: number;
}

export interface LaidOutCluster<T> {
  id: string;
  title: string;
  subtitle?: string;
  originX: number;
  originY: number;
  width: number;
  height: number;
  hexes: PositionedHex<T>[];
}

export interface CanvasLayout<T> {
  clusters: LaidOutCluster<T>[];
  width: number;
  height: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

interface PackedHex<T> {
  item: T;
  x: number;
  y: number;
}
interface Packed<T> {
  hexes: PackedHex<T>[];
  width: number;
  height: number;
}

function packCluster<T>(items: T[]): Packed<T> {
  const n = items.length;
  if (n === 0) return { hexes: [], width: STEP_X, height: HEX_H };
  const cols = clamp(Math.ceil(Math.sqrt(n)), 2, 5);

  const hexes: PackedHex<T>[] = [];
  let maxRight = 0;
  let maxBottom = 0;
  items.forEach((item, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    // Flat-top honeycomb: columns interlock horizontally (¾-width pitch) and every
    // odd column is dropped half a row so the hexes tessellate without gaps.
    const x = col * STEP_X;
    const y = row * STEP_Y + (col % 2 ? STEP_Y / 2 : 0);
    hexes.push({ item, x, y });
    maxRight = Math.max(maxRight, x + HEX_W);
    maxBottom = Math.max(maxBottom, y + HEX_H);
  });
  return { hexes, width: maxRight, height: maxBottom };
}

/** Flow clusters across the canvas (wrap at TARGET_W); resolve hexes to absolute centers. */
export function layoutClusters<T>(clusters: ClusterInput<T>[]): CanvasLayout<T> {
  const packed = clusters.map((c) => ({ input: c, pack: packCluster(c.items) }));

  let cursorX = CANVAS_PAD;
  let cursorY = CANVAS_PAD;
  let rowHeight = 0;
  let canvasRight = 0;

  const out: LaidOutCluster<T>[] = [];

  for (const { input, pack } of packed) {
    const clusterW = Math.max(pack.width, 120);
    const clusterH = TITLE_H + pack.height;

    if (cursorX > CANVAS_PAD && cursorX + clusterW > CANVAS_PAD + TARGET_W) {
      cursorX = CANVAS_PAD;
      cursorY += rowHeight + CLUSTER_GAP_Y;
      rowHeight = 0;
    }

    const originX = cursorX;
    const originY = cursorY;
    const hexTop = originY + TITLE_H;

    const hexes: PositionedHex<T>[] = pack.hexes.map((h) => ({
      item: h.item,
      x: originX + h.x,
      y: hexTop + h.y,
      cx: originX + h.x + HEX_W / 2,
      cy: hexTop + h.y + HEX_H / 2,
    }));

    out.push({
      id: input.id,
      title: input.title,
      subtitle: input.subtitle,
      originX,
      originY,
      width: clusterW,
      height: clusterH,
      hexes,
    });

    cursorX += clusterW + CLUSTER_GAP_X;
    rowHeight = Math.max(rowHeight, clusterH);
    canvasRight = Math.max(canvasRight, originX + clusterW);
  }

  return {
    clusters: out,
    width: canvasRight + CANVAS_PAD,
    height: cursorY + rowHeight + CANVAS_PAD,
  };
}
